/**
 * Modal Component
 * 
 * A reusable modal wrapper that extends Untitled UI Modal with app-specific defaults.
 * Provides title, close button, and pre-built button helpers with internationalization.
 * 
 * @example
 * // Simple usage with direct children
 * <Modal title="Settings">
 *   <div className="space-y-4">
 *     <input placeholder="Name" />
 *     <input placeholder="Email" />
 *   </div>
 * </Modal>
 * 
 * @example
 * // With pre-built buttons
 * <Modal 
 *   title="Delete Item" 
 *   subtitle="This action cannot be undone"
 *   buttons={[
 *     <Modal.CancelButton onCancel={close} />,
 *     <Modal.DeleteButton onDelete={() => { deleteItem(); close(); }} />
 *   ]}
 * >
 *   {t('general.confirmDelete')}
 * </Modal>
 * 
 * @example
 * // Advanced usage with render props for callbacks
 * <Modal title="User Profile">
 *   {({ close, isEntering, isExiting }) => (
 *     <form onSubmit={(e) => { handleSave(e); close(); }}>
 *       <input name="name" />
 *       <div className="mt-4 flex gap-2">
 *         <button type="button" onClick={close}>Cancel</button>
 *         <button type="submit" disabled={isExiting}>
 *           {isExiting ? 'Saving...' : 'Save'}
 *         </button>
 *       </div>
 *     </form>
 *   )}
 * </Modal>
 * 
 * @example
 * // Usage with Link component
 * <Link modal={
 *   <Modal title="Confirmation" hideCloseButton>
 *     {({ close }) => (
 *       <div>
 *         <p>Continue with this action?</p>
 *         <div className="flex gap-2 mt-4">
 *           <Modal.CancelButton onCancel={close} />
 *           <Modal.SaveButton onSave={() => { doAction(); close(); }}>
 *             Continue
 *           </Modal.SaveButton>
 *         </div>
 *       </div>
 *     )}
 *   </Modal>
 * }>
 *   Open Modal
 * </Link>
 * 
 * @props
 * - title: string - Modal header title (required)
 * - subtitle?: string - Optional subtitle below title
 * - children: ReactNode | (props) => ReactNode - Modal content or render function
 * - buttons?: ReactElement[] - Array of button elements for footer
 * - hideCloseButton?: boolean - Hide the X close button (default: false)
 * 
 * @button-helpers
 * - Modal.SaveButton - Primary button with "Save" text (t('general.save'))
 * - Modal.CancelButton - Secondary button with "Cancel" text (t('general.cancel'))  
 * - Modal.DeleteButton - Destructive button with "Delete" text (t('general.delete'))
 * 
 * @render-props (when using function children)
 * - close: () => void - Function to close the modal
 * - isEntering: boolean - True when modal is animating in
 * - isExiting: boolean - True when modal is animating out
 */

import type { ReactElement, ReactNode } from 'react';
import * as React from 'react';
import { Dialog, Modal as UntitledModal } from '@/components/_untitled/application/modals/modal';
import { CloseButton } from '@/components/_untitled/base/buttons/close-button';
import { CancelButton, DeleteButton, RejectButton, SaveButton } from '@/components/ui/ButtonHelpers';

interface ModalProps {
  title: string;
  subtitle?: string;
  children: ReactNode | ((props: { close: () => void; isEntering: boolean; isExiting: boolean }) => ReactNode);
  buttons?: ReactElement[];
  hideCloseButton?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

const ModalComponent = ({ title, subtitle, children, buttons, hideCloseButton = false, size = 'md' }: ModalProps) => {
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-3xl',
    xl: 'max-w-6xl',
    full: 'w-[calc(100vw-4rem)] h-[calc(100vh-4rem)] max-w-none max-h-none'
  };

  return (
    <UntitledModal className={sizeClasses[size]}>
      {({ isEntering, isExiting }) => (
        <Dialog className={`bg-white rounded-xl shadow-xl overflow-hidden relative ${size === 'full' ? 'flex flex-col h-full' : ''}`}>
          {({ close }) => (
            <>
              {!hideCloseButton && (
                <CloseButton
                  size="md"
                  className="absolute top-4 end-4 z-10"
                  onPress={close}
                />
              )}
              <div className={`w-full ${size === 'full' ? 'flex flex-col h-full' : 'p-6'}`}>
                {size === 'full' ? (
                  <>
                    <div className="px-6 pt-6 pb-4 shrink-0">
                      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                      {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
                    </div>
                    <div className="flex-1 overflow-hidden">
                      {typeof children === 'function'
                        ? children({ close, isEntering, isExiting })
                        : children
                      }
                    </div>
                    {buttons && (
                      <div className="flex gap-3 px-6 py-4 border-t border-gray-200 shrink-0">
                        {buttons.map((button, index) => (
                          <div key={index} className="flex-1">
                            {React.cloneElement(button, {
                              onClose: close,
                              ...button.props
                            })}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="mb-4">
                      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                      {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
                    </div>
                    <div className="mb-6">
                      {typeof children === 'function'
                        ? children({ close, isEntering, isExiting })
                        : children
                      }
                    </div>
                    {buttons && (
                      <div className="flex gap-3 pt-6 border-t border-gray-200">
                        {buttons.map((button, index) => (
                          <div key={index} className="flex-1">
                            {React.cloneElement(button, {
                              onClose: close,
                              ...button.props
                            })}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </Dialog>
      )}
    </UntitledModal>
  );
};


// Export Modal with button helpers attached
export const Modal = Object.assign(ModalComponent, {
  SaveButton,
  CancelButton,
  DeleteButton,
  RejectButton,
});